import { useCallback, useMemo, useState } from "react";

import {
    Box,
    Button,
    Container,
    FormControl,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import Fuse from "fuse.js";

import DateRangePicker from "../DateRangePicker";
import { ITEM_HEIGHT } from "../FiltersReport";
import FilterDropdown, { FilterChips } from "../FiltersDropdown";
import EventsTable from "./EventsTable";
import EventsComparison from "./EventsComparison";
import ToggleButtonTitled from "../ToggleButtonTitled";

import Icons from "../../helpers/icons";
import Colours from "../../helpers/colours";
import { CATEGORY } from "../../helpers/events";
import { dayjsUTC, MIN_DATE, MAX_DATE } from "../../helpers/dates";
import {
    PresetDates,
    PRESET_CHOOSE_MONTH,
    PRESET_CUSTOM,
    PRESET_MONTH_TO_DATE,
    PRESET_YEAR_TO_DATE,
    PRESET_YESTERDAY,
    PRESET_TOMORROW,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_FULL_MONTH,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_12_MONTHS,
    PRESET_LAST_YEAR,
    PRESET_NEXT_7_DAYS,
    PRESET_NEXT_30_DAYS,
    PRESET_NEXT_MONTH,
    PRESET_NEXT_3_MONTHS,
    PRESET_NEXT_6_MONTHS,
    PRESET_NEXT_12_MONTHS,
} from "../../helpers/filters";

const dayjs = dayjsUTC;

const EVENTS_PRESET_OPTIONS = [
    PRESET_CHOOSE_MONTH,
    PRESET_MONTH_TO_DATE,
    PRESET_YEAR_TO_DATE,
    PRESET_YESTERDAY,
    PRESET_TOMORROW,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_FULL_MONTH,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_12_MONTHS,
    PRESET_LAST_YEAR,
    PRESET_NEXT_7_DAYS,
    PRESET_NEXT_30_DAYS,
    PRESET_NEXT_MONTH,
    PRESET_NEXT_3_MONTHS,
    PRESET_NEXT_6_MONTHS,
    PRESET_NEXT_12_MONTHS,
    PRESET_CUSTOM,
];
const LONGEST_PRESET = EVENTS_PRESET_OPTIONS.reduce(
    (a, b) => (a.length >= b.length ? a : b),
    "",
);

const transformOptions = (obj, formatLabel = (value) => value.label) => {
    return Object.entries(obj)
        .sort((a, b) => {
            const orderA = a[1]?.order ?? 0;
            const orderB = b[1]?.order ?? 0;
            if (orderA !== orderB) return orderA - orderB;
            return (formatLabel(a[1]) ?? "").localeCompare(
                formatLabel(b[1]) ?? "",
            );
        })
        .reduce((acc, [key, value]) => {
            acc[key] = formatLabel(value);
            return acc;
        }, {});
};

const eventOverlapsRange = (event, start, end) => {
    const eventStart = dayjs(event.FromDate);
    const eventEnd = dayjs(event.ToDate);

    return (
        eventStart.isBetween(start, end, "day", "[]") ||
        eventEnd.isBetween(start, end, "day", "[]") ||
        (eventStart.isBefore(start, "day") && eventEnd.isAfter(end, "day"))
    );
};

const COMPARISON_MODE = {
    NONE: "none",
    SAME_TIME_LAST_YEAR: "same_time_last_year",
    SAME_MONTH_LAST_YEAR: "same_month_last_year",
};

const EventsList = ({
    events,
    eventsExtra,
    loading,
    isAdmin,
    onAddNew,
    onEventClicked,
    onDelete,
    onBulkDelete,
    onBulkDuplicate,
    onToggle = () => {},
}) => {
    const [querySearch, setQuerySearch] = useState("");
    const [presetDate, setPresetDate] = useState(PRESET_MONTH_TO_DATE);
    const [fromDate, setFromDate] = useState(
        dayjs(PresetDates(PRESET_MONTH_TO_DATE).fromDay),
    );
    const [toDate, setToDate] = useState(
        dayjs(PresetDates(PRESET_MONTH_TO_DATE).toDay),
    );
    const [activeFilters, setActiveFilters] = useState({});
    const [comparisonMode] = useState(COMPARISON_MODE.NONE);
    const [comparisonOpen, setComparisonOpen] = useState(false);

    const handlePresetChange = (value) => {
        setPresetDate(value);
        if (value !== PRESET_CUSTOM) {
            const { fromDay, toDay } = PresetDates(value);
            setFromDate(dayjs(fromDay));
            setToDate(dayjs(toDay));
        }
    };

    const legacyOwnerOptions = useMemo(() => {
        const opts = {};
        const contacts = eventsExtra?.Contacts ?? {};
        events.forEach((ev) => {
            const v = ev.Owner;
            if (v && v !== "Aro" && v !== "Hotel" && !opts[v]) {
                const contact = contacts[v];
                if (!contact) opts["ext"] = "Please Reset - External";
                else
                    opts[v] =
                        `Please Reset - ${contact.FirstName ?? ""} ${contact.LastName ?? ""}`;
            }
        });
        return opts;
    }, [events, eventsExtra]);

    const FILTERS = {
        Category: {
            label: "Category",
            options: transformOptions(CATEGORY),
            sortFn: (a, b) => {
                const orderA = CATEGORY[a]?.order ?? 0;
                const orderB = CATEGORY[b]?.order ?? 0;
                if (orderA !== orderB) return orderA - orderB;
                return (CATEGORY[a]?.label ?? a).localeCompare(
                    CATEGORY[b]?.label ?? b,
                );
            },
        },
        Owner: {
            label: "Owner",
            options: { Aro: "Aro", Hotel: "Hotel", ...legacyOwnerOptions },
        },
    };

    const matchesActiveFilters = useCallback(
        (ev) => {
            const eventFilters = {
                Category: ev.Category,
                Owner: ev.Owner,
            };

            return Object.entries(activeFilters).every(([key, filters]) => {
                if (filters.length === 0) return true;

                const eventValue = eventFilters[key];

                if (Array.isArray(eventValue)) {
                    return eventValue.some((val) =>
                        filters.map(String).includes(String(val)),
                    );
                }

                return filters.map(String).includes(String(eventValue));
            });
        },
        [activeFilters],
    );

    const applySearch = useCallback(
        (data) => {
            if (!querySearch) return data;

            const fuseSearch = new Fuse(data, {
                threshold: 0.2,
                keys: [
                    { name: "Name" },
                    { name: "Description" },
                    { name: "FromDateString" },
                    { name: "FromDate" },
                ],
            });

            return fuseSearch.search(querySearch).map(({ item }) => item);
        },
        [querySearch],
    );

    const searchedEvents = useMemo(() => {
        const filtered = events.filter((ev) => {
            const inDateRange =
                !fromDate || !toDate
                    ? true
                    : eventOverlapsRange(ev, fromDate, toDate);

            return inDateRange && matchesActiveFilters(ev);
        });

        return applySearch(filtered);
    }, [events, fromDate, toDate, matchesActiveFilters, applySearch]);

    const comparisonPeriod = useMemo(() => {
        if (comparisonMode === COMPARISON_MODE.NONE) return null;

        if (comparisonMode === COMPARISON_MODE.SAME_TIME_LAST_YEAR) {
            const start = dayjs(fromDate).subtract(1, "year");
            const end = dayjs(toDate).subtract(1, "year");
            return {
                start,
                end,
                title: `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`,
            };
        }

        const monthBase = dayjs(fromDate).subtract(1, "year");
        const start = monthBase.startOf("month");
        const end = monthBase.endOf("month");
        return {
            start,
            end,
            title: start.format("MMMM YYYY"),
        };
    }, [comparisonMode, fromDate, toDate]);

    const previousYearEvents = useMemo(() => {
        if (!comparisonPeriod) return [];

        const filtered = events.filter((ev) => {
            return (
                eventOverlapsRange(
                    ev,
                    comparisonPeriod.start,
                    comparisonPeriod.end,
                ) && matchesActiveFilters(ev)
            );
        });

        return applySearch(filtered);
    }, [events, comparisonPeriod, matchesActiveFilters, applySearch]);

    return (
        <Container maxWidth={false}>
            <Stack
                direction="row"
                justifyContent={"space-between"}
                alignItems="center"
                sx={{ mt: 2, mb: 2 }}
            >
                <Typography variant="h4">Events List</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    {(() => {
                        return (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <ToggleButtonTitled
                                        TITLE={null}
                                        values={{
                                            list: {
                                                label: "List",
                                                icon: <Icons.List />,
                                            },
                                            calendar: {
                                                label: "Calendar",
                                                icon: <Icons.EventsCalendar />,
                                            },
                                        }}
                                        valueSet="list"
                                        onChange={(_, val) => onToggle(val)}
                                        size="small"
                                        ITEM_HEIGHT={ITEM_HEIGHT}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    sx={{
                                        height: ITEM_HEIGHT,
                                        color: "black",
                                        pl: 4,
                                        pr: 4,
                                        textTransform: "none",
                                        maxWidth: "170px",
                                    }}
                                    disabled={loading || !eventsExtra?.Contacts}
                                    onClick={onAddNew}
                                >
                                    <Icons.Add sx={{ paddingRight: 1 }} />
                                    Add New
                                </Button>
                            </>
                        );
                    })()}
                </Stack>
            </Stack>

            <Typography variant="subtitle1" color="initial">
                Shows events in list view based on filters below.
            </Typography>
            <br />

            <Stack direction="row" spacing={1}>
                <Stack>
                    <Typography>Time Period</Typography>
                    <FormControl size="small">
                        <Select
                            value={presetDate}
                            onChange={(e) => handlePresetChange(e.target.value)}
                            sx={{ height: ITEM_HEIGHT }}
                            MenuProps={{ sx: { maxHeight: "280px" } }}
                            renderValue={(val) => (
                                // CSS grid: the hidden span (longest option) sizes the trigger width automatically.
                                <Box sx={{ display: "inline-grid" }}>
                                    <Box
                                        component="span"
                                        aria-hidden
                                        sx={{
                                            gridArea: "1 / 1",
                                            visibility: "hidden",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {LONGEST_PRESET}
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{ gridArea: "1 / 1" }}
                                    >
                                        {val}
                                    </Box>
                                </Box>
                            )}
                        >
                            {EVENTS_PRESET_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
                <Stack>
                    <Typography>Select Dates</Typography>
                    <DateRangePicker
                        pickByMonthYear={presetDate === PRESET_CHOOSE_MONTH}
                        isRangePicker={presetDate !== PRESET_CHOOSE_MONTH}
                        boxProps={{ sx: { height: ITEM_HEIGHT } }}
                        fromDateProps={{
                            value: fromDate,
                            minDate: MIN_DATE,
                            maxDate: MAX_DATE,
                        }}
                        onChangeFromDate={(newValue) => {
                            if (presetDate === PRESET_CHOOSE_MONTH) {
                                const { fromDay, toDay } = PresetDates(
                                    PRESET_CHOOSE_MONTH,
                                    {
                                        year: newValue.year(),
                                        month: newValue.month(),
                                    },
                                );
                                setFromDate(dayjs(fromDay));
                                setToDate(dayjs(toDay));
                            } else {
                                setFromDate(newValue);
                                setPresetDate(PRESET_CUSTOM);
                            }
                        }}
                        toDateProps={{
                            value: toDate,
                            minDate: MIN_DATE,
                            maxDate: MAX_DATE,
                        }}
                        onChangeToDate={(newValue) => {
                            setToDate(newValue);
                            setPresetDate(PRESET_CUSTOM);
                        }}
                    />
                </Stack>

                <Stack>
                    <Typography>Filters</Typography>
                    <FilterDropdown
                        title={"Filters"}
                        buttonProps={{
                            sx: { height: ITEM_HEIGHT },
                        }}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                        filters={FILTERS}
                    />
                </Stack>

                {isAdmin && (
                    <Stack>
                        <Typography>Compare</Typography>
                        <Button
                            variant="outlined"
                            sx={{
                                height: ITEM_HEIGHT,
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                borderColor: Colours.midLightText,
                                color: Colours.textColour,
                            }}
                            onClick={() => setComparisonOpen(true)}
                        >
                            <Typography>Same Time Last Year</Typography>
                        </Button>
                    </Stack>
                )}

                <Stack>
                    <Typography sx={{ visibility: "hidden" }}>
                        Search
                    </Typography>
                    <TextField
                        id="search-bar"
                        className="text"
                        onInput={(e) => setQuerySearch(e.target.value)}
                        value={querySearch}
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        sx={{
                            maxWidth: "170px",
                            "& .MuiInputBase-root": {
                                height: ITEM_HEIGHT,
                            },
                        }}
                        autoComplete="off"
                    />
                </Stack>
            </Stack>

            <FilterChips
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filters={Object.fromEntries(
                    Object.keys(FILTERS).map((filter) => [
                        filter,
                        {
                            format: (opt) => FILTERS[filter].options[opt],
                            label: FILTERS[filter].label,
                        },
                    ]),
                )}
            />

            <EventsTable
                handleDelete={onDelete}
                eventsExtra={eventsExtra}
                events={searchedEvents}
                loading={loading}
                handleEventClicked={onEventClicked}
                enableBulkSelection={isAdmin}
                isAdmin={isAdmin}
                onBulkDelete={onBulkDelete}
                onBulkDuplicate={onBulkDuplicate}
            />

            {comparisonPeriod && (
                <>
                    <br />
                    <Typography variant="h6">
                        {comparisonPeriod.title}
                    </Typography>
                    <EventsTable
                        handleDelete={onDelete}
                        eventsExtra={eventsExtra}
                        events={previousYearEvents}
                        loading={loading}
                        handleEventClicked={onEventClicked}
                    />
                </>
            )}

            <EventsComparison
                open={comparisonOpen}
                onClose={() => setComparisonOpen(false)}
                events={events}
                eventsExtra={eventsExtra}
                loading={loading}
                handleDelete={onDelete}
                handleEventClicked={onEventClicked}
                baseFromDate={fromDate}
                baseToDate={toDate}
                isAdmin={isAdmin}
            />
        </Container>
    );
};

export default EventsList;
